import { JSX, useState, useEffect, useRef } from 'react';
import WindowBar from '../common/windowBar/windowBar';
import TodoIcon from '../assets/images/svgs/bookmark-Pixel.svg'
import TodoManager from '../todolist/data/todoManager';
import { TodoTask } from '../todolist/add-todo/addTodoList';
import TodoView from './todoView/todoView';
import MenuButton from '../common/windowMenu/menuButton';
import TimeOptions from './menu/time/timeOptions';
import './todoPage.css';
import { timeOption } from './menu/time/timeOptions';
import CategoryDropdown from '../todolist/category/categoryDropdown';
import { categories, categoryProps } from '../todolist/category/category';
import EditTodo from './editTodo';

type SelectedOption = 'All' | 'Yearly' | 'Monthly' | 'Weekly' | 'Daily' | 'Work' | 'Personal' | 'Shopping' | 'Birthday';

const TodoPage = ({ setClosePage }: { setClosePage: () => void }): JSX.Element => {

    const todoManager = TodoManager.getInstance();
    const [todos, setTodos] = useState<TodoTask[]>([]);
    const [displayTodos, setDisplayTodos] = useState<TodoTask[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openMenu, setOpenMenu] = useState<JSX.Element>(<></>);
    const [selectedOption, setSelectedOption] = useState<SelectedOption>('All');
    const [editTodoPage, setEditTodoPage] = useState<JSX.Element>(<></>);
    
    const refreshTodos = async () => {
        try {
            const fetchedTodos = await todoManager.getTodos(); // Now waits for initialization
            setTodos([...fetchedTodos]);
        } catch (error) {
            console.error('Failed to fetch todos:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refreshTodos();
    }, []);

    useEffect(() => {
        // Subscribe to updates
        const unsubscribe = todoManager.subscribe(refreshTodos);
        
        // Initial load
        refreshTodos();

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    const setOpenMenuElement = (element: JSX.Element) => {
        setOpenMenu(element);
    }
    const timeButtonRef = useRef<HTMLDivElement>(null);
    const categoryButtonRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const[title, setTitle] = useState('TodoList: All');
    const[selectedTime, setSelectedTime] = useState<timeOption>(null);
    const[timeTabClickOpen, setTimeTabOpen] = useState(false);
    const[categoryTabClickOpen, setCategoryTabOpen] = useState(false);
    const[selectedCategory, setSelectedCategory] = useState<categoryProps>(null);
    const[isTimeSelected, setTimeSelected] = useState(false);
    const[isCategorySelected, setCategorySelected] = useState(false);
    const[isTodoEdit, setIsTodoEdit] = useState(false);

    const closeTimeWindow = () => {
        setTimeTabOpen(false);
        setOpenMenuElement(<></>);
    }

    const handleTimeClick = () => {
        if (timeButtonRef.current && containerRef.current) {
            const buttonRect = timeButtonRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            
            // Calculate position relative to container
            setMenuPosition({
                ...menuPosition,  // Spread existing properties
                top: buttonRect.bottom - containerRect.top + 10,
                left: buttonRect.left - containerRect.left
            });

            console.log(`top: ${menuPosition.top}`)

            setTimeTabOpen(!timeTabClickOpen);
            setCategoryTabOpen(false);
            setOpenMenuElement(<TimeOptions selectedTimeOption={selectedTime} setSelectedTimeOption={setSelectedTime} closeWindow={closeTimeWindow}/>);
        }
    };

    const selectCategory = (category: categoryProps) => {
        setCategoryTabOpen(false);
        setSelectedCategory(category);
        setOpenMenuElement(<></>);
        setCategorySelected(true);
        if(category.type === 'Birthday')
            setSelectedOption('Birthday')
        else if(category.type === 'Shopping')
            setSelectedOption('Shopping')
        else if(category.type === 'Personal')
            setSelectedOption('Personal')
        else if(category.type === 'Work')
            setSelectedOption('Work')
    }

    const hanleCategoryClick = () => {
        if (categoryButtonRef.current && containerRef.current) {
            const buttonRect = categoryButtonRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            
            // Calculate position relative to container
            setMenuPosition({
                ...menuPosition,  // Spread existing properties
                top: buttonRect.bottom - containerRect.top + 10,
                left: buttonRect.left - containerRect.left
            });

            console.log(menuPosition.top);

            setTimeTabOpen(false);
            setSelectedTime(null);
            setCategoryTabOpen(!categoryTabClickOpen);
            setOpenMenuElement(<CategoryDropdown onCategorySelect={selectCategory} onClose={()=> closeCategoryTab}/>);
        }
    }

    const closeCategoryTab = () => {
        setOpenMenuElement(<></>);
        setCategoryTabOpen(false);
    }

    const handleAllClick = () => {
        setTimeTabOpen(false);
        setCategoryTabOpen(false);
        setSelectedTime(null);
        setSelectedCategory(null);
        setOpenMenuElement(<></>);
        filterTodos();
    }

    useEffect(() => {
        if(selectedTime === null && selectedCategory === null) {
            setTitle('TodoList: All');
            setSelectedOption('All');
        } else if(selectedTime != null) {
            setTitle(`TodoList: ${selectedTime}`);
            setSelectedOption(selectedTime);
        } else if(selectedCategory != null) {
            setTitle(`TodoList: ${selectedCategory.type}`);
            setSelectedOption(selectedCategory.type as SelectedOption);
        }
        filterTodos();
    },[selectedTime, selectedCategory]);

    useEffect(() => {
        filterTodos();
    },[todos]);

    const filterTodos = () => {
        console.log(selectedTime)
        if (selectedTime === null && selectedCategory === null) {
            setDisplayTodos(todos);
        } else if(selectedTime === 'Yearly') {
            const filteredTodos = todos.filter(todo => new Date(todo.date).getFullYear() === new Date().getFullYear());
            setDisplayTodos(filteredTodos);
        }
        else if(selectedTime === 'Monthly') {
            const filteredTodos = todos.filter(todo => new Date(todo.date).getFullYear() === new Date().getFullYear() &&
            new Date(todo.date).getMonth() === new Date().getMonth());
            setDisplayTodos(filteredTodos);
        }
        else if(selectedTime === 'Weekly') {
            const today = new Date();
            const firstDayOfWeek = new Date(today);
            firstDayOfWeek.setDate(today.getDate() - today.getDay()); // Get Sunday
            
            const lastDayOfWeek = new Date(firstDayOfWeek);
            lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6); // Get Saturday
        
            const filteredTodos = todos.filter(todo => 
                new Date(todo.date) >= firstDayOfWeek && 
                new Date(todo.date) <= lastDayOfWeek
            );
            setDisplayTodos(filteredTodos);
        }
        else if(selectedTime === 'Daily') {
            const filteredTodos = todos.filter(todo => new Date(todo.date).getDate() === new Date().getDate());
            setDisplayTodos(filteredTodos);
        } else if(selectedCategory.type === 'Work') {
            console.log("selected Work");
            const filteredTodos = todos.filter(todo => todo.category === 'Work');
            setDisplayTodos(filteredTodos);
        } else if(selectedCategory.type === 'Birthday') {
            const filteredTodos = todos.filter(todo => todo.category === 'Birthday');
            setDisplayTodos(filteredTodos);
        } else if(selectedCategory.type === 'Personal') {
            const filteredTodos = todos.filter(todo => todo.category === 'Personal');
            setDisplayTodos(filteredTodos);
        } else if(selectedCategory.type === 'Shopping') {
            const filteredTodos = todos.filter(todo => todo.category === 'Shopping');
            setDisplayTodos(filteredTodos);
        }
    };

    useEffect(()=>{
        if(selectedOption === 'Yearly' || selectedOption === 'Monthly' || selectedOption === 'Weekly' || selectedOption === 'Daily') {
            setTimeSelected(true);
            setCategorySelected(false);
        } else if(selectedOption === 'Work' || selectedOption === 'Shopping' || selectedOption === 'Personal' || selectedOption === 'Birthday') {
            setTimeSelected(false);
            setCategorySelected(true);
        } else if(selectedOption === 'All') {
            setTimeSelected(false);
            setCategorySelected(false);
        }
    },[selectedOption])

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const setCloseEditPage = () => {
        setIsTodoEdit(false);
    }

    const openEditPage = (todo: TodoTask) => {
        setIsTodoEdit(true);
        setEditTodoPage(<EditTodo todo={todo} setClosePage={setCloseEditPage}/>)
    }

    return (
        <div className='addTodo-outter outter-container' ref={containerRef}>
          <div className='pixel-container-back'/>
          <div className='pixel-container-top addTodo-container'>
            <div className='addPage-inner'>
                <WindowBar IconSrc={TodoIcon} title={title} onClick={setClosePage}/>
                <div className='main-pixel-container page-content'>
                    <div className='addPage-menu'>
                        <MenuButton title='All' isChosen={selectedOption==='All'} onClick={handleAllClick}/>
                        <div ref={timeButtonRef}>
                            <MenuButton title='Time' isChosen={isTimeSelected} onClick={handleTimeClick}/>
                        </div>
                        <div ref={categoryButtonRef}>
                            <MenuButton title='Category' isChosen={isCategorySelected} onClick={hanleCategoryClick}/>
                        </div>
                    </div>
                    <div className="divider-horizontal"/>
                    <div className='todoPage-todos'>
                        {displayTodos.map(todo => (
                            <TodoView todo={todo}
                                onUpdate={refreshTodos}
                                openEdit={() => openEditPage(todo)}/>
                        ))}
                    </div>
                    <div className="divider-horizontal"/>
                </div>
            </div>
          </div>
          <div className='menu-overlay' style={{
                position: 'absolute',
                top: menuPosition.top,
                left: menuPosition.left,
                zIndex: 1000
            }}>
                {timeTabClickOpen || categoryTabClickOpen ? (openMenu):(<></>)}
                {isTodoEdit? (editTodoPage):(<></>)}
            </div>
        </div>
    ); 
};

export default TodoPage;